import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * QA/testing-only bypass route. Calls the backend test-login endpoint to
 * simulate a Google OAuth login for a given role without the real Google popup.
 *
 * SECURITY: The backend endpoint is gated behind the QA_BYPASS_ENABLED env
 * flag and is disabled in production. If disabled, the backend returns 404
 * and we simply bounce the visitor back to the landing page — they cannot
 * escalate to admin via a URL.
 *
 * Usage (in QA environments only):
 *   /qa-login?role=founder&redirect=/dashboard
 */
export default function QaLogin() {
  const [searchParams] = useSearchParams();
  const { qaLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const role = searchParams.get("role") || "founder";
    const email = searchParams.get("email") || undefined;
    const redirect = searchParams.get("redirect") || (role === "admin" ? "/admin" : "/dashboard");
    qaLogin(role, email)
      .then(() => navigate(redirect, { replace: true }))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) {
          // QA bypass is disabled (production) — silently redirect home
          navigate("/", { replace: true });
        } else if (status === 429) {
          toast.error("Too many attempts. Please wait a moment.");
          navigate("/", { replace: true });
        } else {
          toast.error("Sign-in unavailable.");
          navigate("/", { replace: true });
        }
      });
  }, [searchParams, qaLogin, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center" data-testid="qa-login-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
